import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ArchiveCreateModeDialog } from '../../component/archive-form';
import CardView from '../../component/card/card-view';
import CommonInput from '../../component/form/common-input.component';
import { HeadCell, TableRowFeatures } from '../../component/table/table.component';
import i18n from '../../i18n';
import { Group, Reseller, RestaurantsInGroup, Warrant, WarrantI18N } from '../../models';
// scss
import './scss/group-management.style.scss'
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AuModeSelectType, GroupState } from './group-management-state';
import GroupUnit, { groupUnitData } from './group-unit';
import DeleteDialogComponent from '../../component/dialog/delete-dialog.component';
import ButtonComponent from '../../component/button/button.component';
import CreateGroupDialog from './creat-group.dialog';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { userData } from '../app.page';
import { toJS } from 'mobx';
import moment from 'moment';
import { ConditionType, ConditionsBox, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import { Option } from '../../component/form/select-input-multiple';
import { PopoverEllipsis } from '../../component/popover/popover-ellipsis';
import _ from 'lodash';
import { getResellerFilter } from '../../constants/tool.reseller';
import { getAuthPointI18n, getRestaurantStatusText } from '../../constants/tool-auth-point';
import ScrollTable from '../../component/table/scroll-table';

/**
 * 集团新增入口：先选择建档方式，再进入既有手动新增弹窗。
 * @param useGroupData 集团管理状态
 */
export const GroupCreateEntry = observer(({ useGroupData }: { useGroupData: GroupState }) => {
    return (
        <>
            <ButtonComponent
                variant='contained'
                size='small'
                extraButtonStyle='add-button'
                titleText={i18n.t('general_text_new')}
                action='ADD'
                space={false}
                handleClick={() => useGroupData.openCreateModeDialog()}
            />
            <ArchiveCreateModeDialog
                open={useGroupData.isCreateModeDialogOpen}
                entityType='group'
                onClose={() => useGroupData.closeCreateModeDialog()}
                onManualEntry={() => useGroupData.continueManualCreate()}
            />
            <CreateGroupDialog useGroupData={useGroupData} />
        </>
    );
});

// 集团管理列表组件
const GroupManagement = observer(() => {
    useTranslation();

    // 使用数据管理
    const groupData = useLocalObservable(() => new GroupState());

    // 顶部栏数据
    const topBarState = MobxObjectsManager.get(TopBarState);

    // 解构数据
    const {
        groupsList,
        selectValue,
        isTableLoading,
        showUnit,
        auSelectValue,
        auFilterSelectValue,
        restaurantList,
        enterGroupDetailObj,
        deleteDialogConfig,
        auPointList,
        tableRowButtonsMap,
        auPointsMap,
        conditions,
    } = groupData;

    React.useEffect(() => {
        // 获取集团数据
        groupData.init();
        topBarState.updateGroupTitle('');
        // 组件卸载  卸载数据
        return () => {
            MobxObjectsManager.remove(GroupState);
        }
    }, []);

    React.useEffect(() => {
        groupData.setAuPointsMap(auPointsMap);
        return () => {
        }
    }, [i18n.language]);
    // 搜索ref
    const SearchKeyInputRef = React.useRef<{ searchKey: string, setSelectKey: (val: string) => void; }>();
    // 当前是否存在搜索条件
    const isSearch = conditions.length > 0 || auFilterSelectValue !== '0';

    // 下拉框 左边搜索条件 静态数据
    const filterList: Array<FilterList> = [
        {
            value: 'res_string_id',
            label: i18n.t('group_management_page_text_restaurant_identification_code')
        }, {
            value: 'res_nickname',
            label: i18n.t('ground_staff_component_text_restaurant_name')
        }, {
            value: 'phone',
            label: i18n.t('general_text_phone')
        }, {
            value: 'reg_vat_number',
            label: i18n.t('restaurant_info_page_text_vat_number')
        }, {
            value: 'string_id',
            label: i18n.t('group_management_page_text_group_identification_code')
        }, {
            value: 'nickname',
            label: i18n.t('group_management_page_text_group_name'),
        }, {
            value: 'city',
            label: i18n.t('group_management_page_text_city')
        }, {
            value: 'access',
            label: i18n.t('warrant_page_text_authorization_scheme')
        }, {
            value: 'business_labels',
            label: i18n.t('restaurant_info_page_text_service_instance_tag')
        }, {
            value: 'fiskaly_environment',
            label: i18n.t('warrant_page_text_fiskaly_mode'),
        }
    ];

    // 业务标签数组  静态数据
    const business_labels: FilterList[] = [
        { value: 'POS', label: 'POS' },
        { value: 'OD', label: 'OD' },
        { value: 'Site', label: 'Site' },
        { value: 'MKT', label: 'MKT' },
    ];

    // Fiskaly 模式数组  静态数据
    const fiskaly_environment: FilterList[] = [
        { value: 'true', label: 'Live' },
        { value: 'false', label: 'Test' },
    ];

    // 授权模式切换下拉框  餐馆、集团、授权  静态数据
    const authorizationModeList: Array<FilterList> = [
        {
            value: 'group',
            label: i18n.t('group_management_page_text_group')
        }, {
            value: 'restaurant',
            label: i18n.t('group_management_page_text_restaurant'),
        }];

    // 授权到期类型筛选下拉框  静态数据
    const warrantFilterList: Array<FilterList> = [
        /**
         * 授权过期类型筛选的值
         * 1. @variable 0 默认所有授权
         * 2. @variable 1 已到期
         * 3. @variable 2 未到期（大于90天）
         * 4. @variable 3 临近到期（小于90天）
         * 5. @variable 4 无任何授权
         */
        {
            value: '0',
            label: i18n.t('group_management_page_text_all'),
        }, {
            value: '1',
            label: i18n.t('group_management_page_text_have_expired'),
        }, {
            value: '2',
            label: i18n.t('group_management_page_text_not_due'),
        }, {
            value: '3',
            label: i18n.t('group_management_page_text_near_expiration'),
        }, {
            value: '4',
            label: i18n.t('group_management_page_text_without_any_authorization'),
            disabled: selectValue === 'access'
        }];

    // 表格头
    const tableHeadCell: {
        // 集团模式
        group: HeadCell[];
        // 餐馆模式
        restaurant: HeadCell[];
    } = {
        // 集团表头数据
        group: [
            {
                id: 'belonged_reseller', width: '10%', label: i18n.t('reseller_page_text_reseller'), renderFunction: (row: Group) => {
                    const reseller = _.get(row, 'belonged_reseller.[0]') as Reseller | undefined;
                    return reseller ? `${reseller.first_name} ${reseller.last_name}` : 'Admin';
                }
            },
            { id: 'nickname', width: '12%', label: i18n.t('group_management_page_text_group_name'), sortStatus: groupData.getSortStatus('nickname'), },
            { id: 'string_id', width: '10%', label: i18n.t('group_management_page_text_group_identification_code') },
            {
                id: 'supervisor', width: '12%', label: i18n.t('group_management_page_text_company_representative'),
                renderFunction: (row) => {
                    // 集团负责人
                    let supervisor = '';
                    row?.supervisor?.forEach((item: { first_name: string, last_name: string }) => {
                        supervisor = (supervisor && supervisor + ', ') + (item?.first_name || '') + (item?.last_name || '');
                    });
                    return (
                        row?.supervisor.length > 1 ?
                            <PopoverEllipsis showData={supervisor} shortData={supervisor.split(', ')[0]} />
                            :
                            <>{supervisor}</>
                    )
                }
            },
            { id: 'city', width: '12%', label: i18n.t('group_management_page_text_city') },
            { id: 'reg_vat_number', width: '12%', label: i18n.t('restaurant_info_page_text_vat_number') },
            {
                id: 'tel', width: '12%', label: i18n.t('general_text_phone'),
                renderFunction: (row: Group) => {
                    return <>{row?.tel ? row?.phone_prefix : ''} {row?.tel}</>
                },
            },
            {
                id: '9', width: '12%', label: '',
                renderFunction: (row, index) => {
                    return renderFeatures(row, index)
                }
            }
        ],
        // 餐馆模式表头数据
        restaurant: [
            {
                id: 'belonged_reseller', width: '8%', label: i18n.t('reseller_page_text_reseller'), renderFunction: (row: Group) => {
                    const reseller = _.get(row, 'belonged_reseller.[0]') as Reseller | undefined;
                    return reseller ? `${reseller.first_name} ${reseller.last_name}` : 'Admin';
                }
            },
            { id: 'nickname', width: '10%', label: i18n.t('ground_staff_component_text_restaurant_name') },
            {
                id: 'string_id', width: '5%', label: i18n.t('group_management_page_text_restaurant_identification_code'),
            },
            { id: 'belonged_group_nickname', width: '8%', label: i18n.t('general_text_belonged_group') },
            {
                id: 'authorization_type', width: '5%', label: i18n.t('restaurant_info_page_text_way_of_authorization'),
            },
            {
                id: 'business_labels', width: '5%', label: i18n.t('restaurant_info_page_text_service_instance_tag'),
                renderFunction: (row: RestaurantsInGroup) => <>{(row?.business_labels || [])?.join('，')}</>
            },
            {
                id: 'warrants', width: '35%', label: i18n.t('group_management_page_text_authorization_message'),
                renderFunction: (row: Group) => {
                    return renderRowResWarrant(row?.warrants || []);
                }
            },
            {
                id: 'setting_restaurant_operating_status', width: '5%', label: i18n.t('restaurant_info_page_status'),
                renderFunction: (row: RestaurantsInGroup) => {
                    const settingRestaurantOperatingStatus = row?.setting_restaurant_operating_status || '1'
                    return i18n.t(getRestaurantStatusText(settingRestaurantOperatingStatus))
                }
            },
            {
                id: 'action', label: '', width: '5%',
                renderFunction: (row, index) => {
                    return renderFeatures(row, index)
                }
            }
        ]
    };



    // *********************
    // Extra Function
    // *********************

    // table里面的功能键  集团授权模式
    const renderFeatures = (row: Group, index: number): React.ReactNode => {
        const buttons = auSelectValue === 'group' ? tableRowButtonsMap['group'](row, index) : tableRowButtonsMap['restaurant'](row, index);
        return (
            <TableRowFeatures
                buttons={buttons}
            />
        )
    }

    // 渲染餐厅授权点
    const renderRowResWarrant = (resWarrants: Warrant[]): React.ReactNode => {
        // 找出相应符合条件的国际化授权点
        const findWarrant = (warrants: Warrant[]) => {
            const WarrantsI18N: WarrantI18N[] = (warrants || []).map((item: Warrant) => {
                return Object.assign(item.warranti18N ?? {}, { expireType: item.expireType, limit_point: item.limit_point ?? {} }) || {};
            });
            return WarrantsI18N;
        };
        const i18nWarrants = findWarrant(resWarrants || []);
        return (
            <Box className={'group-card'}>
                {i18nWarrants.map((item, index) => (
                    <WarrantCard key={index} item={item} />
                ))}
            </Box>
        )
    }

    /**
     * 折叠组件展示的内容
     * @param row 行数据
     * @param index 行索引
     * @returns
     */
    const renderExtraCollapseComponent = (row: Data): React.ReactNode => {
        // 获取语言
        const lang = userData.language;
        // 当前集团授权组装数据
        const curGroupWarrant: Pick<Group, '_id' | 'string_id' | 'nickname' | 'warrants'> = {
            nickname: row.nickname || '',
            string_id: row?.string_id || '',
            _id: row?._id || '',
            warrants: row?.warrants || []
        }
        // 当前集团下所有餐厅的授权数据
        const curGroupResWarrant: RestaurantsInGroup[] = row?.restaurants || [];

        return (
            <Box className={'collapse-content'}>
                <AccordionsComp language={lang} groupWarrant={curGroupWarrant} restaurantWarrant={curGroupResWarrant} />
            </Box>
        )
    }

    // 渲染集团列表组件
    const RenderComponent = (): JSX.Element => {
        let newFilterList = filterList as SearchKeyInputItems[];
        newFilterList.forEach((item) => {
            if (item.value === 'access') {
                item.options = auPointList ?? [];
                item.type = 'SELECT';
            } else if (item.value === 'business_labels') {
                item.options = business_labels;
                item.type = 'SELECT';
            } else if (item.value === 'fiskaly_environment') {
                item.type = 'SELECT';
                item.options = fiskaly_environment
            }
            else {
                item.type = 'TEXT';
            }
        })
        // Admin 需要加入经销商筛选条件
        newFilterList = getResellerFilter(newFilterList);
        const list = auSelectValue === 'restaurant' ? restaurantList || [] : groupsList || []
        return (
            <Box>
                <Box className='table-top-tool'>
                    <Box className={'left-tool'}>
                        {/* 新增按钮 */}
                        <GroupCreateEntry useGroupData={groupData} />
                        <SearchKeyInput
                            ref={SearchKeyInputRef}
                            style={{ marginLeft: '8px' }}
                            items={newFilterList}
                            conditions={toJS(conditions)}
                            onSearchKeyChange={(key) => groupData.setSelectValue(key)}
                            onSearch={(conditions: ConditionType[]) => {
                                groupData.setConditions(conditions);
                            }}
                        />
                        {/* 下拉框 授权到期时间筛选框 */}
                        <Box display={'flex'} alignItems={'center'} sx={{ marginLeft: 2, minWidth: '110px' }}>
                            <CommonInput
                                inputType={'SELECT'}
                                items={warrantFilterList ?? []}
                                label={i18n.t('group_management_page_expiration_time_of_authorization_point')}
                                size={'small'}
                                fullWidth
                                value={auFilterSelectValue}
                                onChange={(e) => {
                                    const selectVal = e.target.value;
                                    groupData.setAuFilterSelectValue(selectVal);
                                }}
                            />
                        </Box>
                    </Box >
                    <Box className={'right-tool'}>
                        <CommonInput
                            inputType={'SELECT'}
                            items={authorizationModeList ?? []}
                            className={'select-input'}
                            size={'small'}
                            label={i18n.t('group_management_page_text_authorization_mode')}
                            fullWidth
                            value={auSelectValue}
                            onChange={(e) => {
                                const selectVal = e.target.value;
                                groupData.setAuSelectValue(selectVal as AuModeSelectType);
                            }}
                        />
                    </Box>
                </Box >
                <Box>
                    <ConditionsBox conditions={toJS(conditions)} onDelete={(conditions) => {
                        groupData.setConditions(conditions);
                        SearchKeyInputRef?.current?.setSelectKey?.('');
                    }} />
                </Box>
                {/* 集团表格 */}
                {
                    isSearch ?
                        <ScrollTable
                            {...{
                                // 请求数据
                                handleLoadMore: () => groupData.handleLoadMore(),
                                // 表格复选框控制  集团列表时显示  餐馆列表时隐藏
                                showChecked: +(auSelectValue === 'group'),
                                // 表格数据
                                list,
                                // 表格头
                                headCellList: tableHeadCell[auSelectValue],
                                // 表格加载
                                isTableLoading,
                                // 表格批量删除
                                handleDeleteAll: async (selected) => {
                                    // 表格批量删除
                                    await groupData.handleDelete(selected, true);
                                },
                                // 批量删除弹窗在打开时展示集团名称和删除限制
                                getDeleteDialogDesc: (selected) => {
                                    return groupData.getBatchDeleteDialogDescription(selected as Group[]);
                                },
                                // 含餐厅集团不允许提交批量删除
                                canDeleteSelected: (selected) => {
                                    return !(selected as Group[]).some((group) => groupData.hasRestaurants(group));
                                },
                                // 渲染的折叠组件
                                renderExtraCollapseComponent: auSelectValue === 'group' ? renderExtraCollapseComponent : undefined,
                                // 表格排序
                                handleSort: (headCell) => {
                                    groupData.handleSort(headCell);
                                },
                                // 表格行可展开
                                hasExpand: auSelectValue === 'group' ? true : false,

                                // 开启折叠组件 防止选择展开按钮会框选到表格行复选框
                                isUseFold: auSelectValue === 'group' ? true : false,
                                // 表格可编辑class类
                                extraClass: 'extraTable',
                                // 是否有更多数据
                                isNoMoreData: groupData.isNoMoreData,
                                // 表格加载中
                                isTableLoadingNext: groupData.isTableLoadingNext,
                                tableHeight: auSelectValue === 'group' ? 'calc(100vh - 200px)' : 'calc(100vh - 150px)'
                            }}
                        />

                        :
                        <CardView contentRender={(
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 'calc(100vh - 300px)'
                            }}>
                                {i18n.t('general_text_enter_filter_source')}
                            </Box>)} />
                }
                {/* 是否删除所选择项目 */}
                <DeleteDialogComponent
                    {...{
                        title: i18n.t('general_text_tips'),
                        desc: deleteDialogConfig.desc,
                        confirmDisabled: deleteDialogConfig.confirmDisabled,
                        open: deleteDialogConfig.visible,
                        onCancel: () => groupData.updateDeleteDialogConfig({
                            visible: false,
                        }),
                        onConfirm: () => deleteDialogConfig.handleClick?.(),
                    }}
                />
            </Box >
        )
    }

    // *********************
    // Render View
    // *********************

    return (
        showUnit ?
            (<GroupUnit goBack={() => {
                groupUnitData.clearGroupData();
                groupUnitData.updatePanel(0);
                groupData.goBack();
            }} group={enterGroupDetailObj.groupData} />)
            : (<CardView
                {...{
                    className: `group-management-page ${showUnit ? 'group-unit1' : ''} `,
                    contentRender: RenderComponent(),
                }}
            />)
    )
})

export default GroupManagement;

// 获取多选输入框的值
export function getMulSelectValue(value: string[], options: Option[]) {
    const selectValue: any[] = [];
    options.forEach((item) => {
        if (item.value && value && value.includes(item.value)) {
            selectValue.push(item);
        }
    });
    return selectValue
}

// 授权点小椭圆卡片
const WarrantCard = ({ item }: { item: WarrantI18N }) => {

    // 筛选出国际化字段名
    const filterWarrantI18nName = getAuthPointI18n(item.name || [])

    return (
        <Tooltip
            title={`${moment(item.limit_point?.start_date).format('DD/MM/YYYY') || ''} ~ ${moment(item.limit_point?.end_date).format('DD/MM/YYYY') || ''}`}
            placement='top'
            arrow
        >
            <Box className='card'>
                <Chip
                    sx={{
                        color: '#fff',
                        fontSize: '14px',
                        height: '45px',
                        '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'pre',
                        },
                    }}
                    label={`${filterWarrantI18nName}\n${moment(item.limit_point?.start_date).format('DD/MM/YYYY') || ''} ~ ${moment(item.limit_point?.end_date).format('DD/MM/YYYY') || ''}`}
                    className={'card-box-shadow'}
                    color={getCardBg(item.expireType)} />
            </Box>
        </Tooltip>
    )
}

// 返回授权点卡片颜色
const getCardBg = (dateType: 'was_due' | 'not_yet_due' | 'soon' | undefined) => {
    switch (dateType) {
        case 'was_due':
            return 'error';
        case 'not_yet_due':
            return 'success';
        case 'soon':
            return 'warning';
        default:
            return 'primary';
    }
}

// 手风琴组件  用于展示集团和餐厅的授权
const AccordionsComp: React.FC<AccordionProps> = (props: AccordionProps): JSX.Element => {

    const { restaurantWarrant } = props;

    const [expanded, setExpanded] = React.useState<string | false>(false);

    /* ******************** 依次淡入动画配置 Start **************** */
    const [animate, setAnimate] = useState<boolean>(false);
    const listRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const list = listRef.current?.children;
        if (list) {
            for (const item of list) {
                (item as HTMLDivElement).style.opacity = '0';
            }
        }
    }, []);

    useEffect(() => {
        setAnimate(true);
    }, []);

    useEffect(() => {
        if (animate) {
            const { children } = listRef.current!;
            for (let i = 0; i < children.length; i++) {
                (children[i] as HTMLDivElement).style.setProperty(`animation-delay`, `${0.050 * i}s`);
            }
        }
    }, [animate]);
    /* ******************** 依次淡入动画配置 End **************** */

    // 手风琴展开
    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        event.stopPropagation();
        setExpanded(isExpanded ? panel : false);
    }

    // 找出可以展示的餐厅授权数据
    const findShowWarrants = (resWarrants: Warrant[]): WarrantI18N[] => {
        // 操作的授权
        const WarrantsI18N: WarrantI18N[] = [];
        // 找到国际化
        (resWarrants ?? [])?.forEach((item: Warrant) => {
            // expireType 授权到期类型
            WarrantsI18N.push(Object.assign(item.warranti18N || {}, { expireType: item.expireType, limit_point: item.limit_point ?? {} }) ?? {});
        });
        return WarrantsI18N;
    }

    return (
        <Box>
            {/* \\\\\\\\\\\\\\\ ↓ TODO 集团授权点暂无数据  所以暂时隐藏掉 ↓ /////////////////// */}
            {/* <Box sx={{ marginBottom: 3 }} className={'fade-in-right'}>
                <Accordion expanded={expanded === groupWarrant?.string_id!} onChange={handleChange(groupWarrant?.string_id || '')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1bh-content"
                        id="panel1bh-header"
                    >
                        <Typography sx={{ marginLeft: 2, flexShrink: 0, fontWeight: 'bold' }}>
                            {`${i18n.t('group_management_page_text_group')}`}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', marginLeft: 2 }}>{groupWarrant?.nickname || ''}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box className={'group-card'} sx={{ paddingLeft: 8 }}>
                            {
                                findShowWarrants(groupWarrant?.warrants || []).length > 0 ?
                                    findShowWarrants(groupWarrant?.warrants || []).map((auItem, _auIndex) => {
                                        return (
                                            <Box className='card' key={_auIndex}>
                                                <Chip
                                                    sx={{ color: '#fff' }}
                                                    label={`${auItem?.name?.[0]?.value || ''}`}
                                                    color={
                                                        auItem.expireType === 'was_due' ? 'error' :
                                                            auItem.expireType === 'not_yet_due' ? 'success' :
                                                                auItem.expireType === 'soon' ? 'warning' :
                                                                    'primary'
                                                    } />
                                            </Box>
                                        )
                                    }) : i18n.t('general_text_no_or_more_data')
                            }
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Box> */}
            {/* \\\\\\\\\\\\\\\ ↑ TODO 集团授权点暂无数据  所以暂时隐藏掉 ↑ /////////////////// */}

            {/* 餐厅授权数据 */}
            <Box className={'accordion-box'} component={'div'} ref={listRef}>
                {
                    restaurantWarrant && restaurantWarrant?.length > 0 ?
                        (restaurantWarrant || []).map((item, index) => {
                            const showWarrants = findShowWarrants(item.warrants || []);
                            return (
                                <Box key={index} sx={{ margin: '5px 0' }} className={'fade-in-right'}>
                                    <Accordion expanded={expanded === item?.string_id!} onChange={handleChange(item?.string_id || '')}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls='panel1bh-content'
                                            id={item?.string_id || 'panel2bh-header'}
                                        >
                                            <Grid container spacing={1}>
                                                <Grid item display={'flex'} md={3}>
                                                    <Typography sx={{ flexShrink: 0, fontWeight: 'bold' }}
                                                    >
                                                        {i18n.t('group_management_page_text_restaurant')}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: 'text.secondary',
                                                            marginLeft: 2,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >{item?.nickname || ''}</Typography>
                                                </Grid>
                                                <Grid item display={'flex'} md={3}>
                                                    <Typography sx={{ flexShrink: 0, fontWeight: 'bold' }}>
                                                        {i18n.t('ground_staff_component_text_restaurant_code')}
                                                    </Typography>
                                                    <Typography sx={{ color: 'text.secondary', marginLeft: 2 }}>{item?.string_id || ''}</Typography>
                                                </Grid>
                                                <Grid item display={'flex'} md={3}>
                                                    <Typography sx={{ flexShrink: 0, fontWeight: 'bold' }}>
                                                        {i18n.t('restaurant_info_page_text_way_of_authorization')}
                                                    </Typography>
                                                    <Typography sx={{ color: 'text.secondary', marginLeft: 2 }}>{item?.authorization_type || ''}</Typography>
                                                </Grid>
                                                <Grid item display={'flex'} md={3}>
                                                    <Typography sx={{ flexShrink: 0, fontWeight: 'bold' }}>
                                                        {i18n.t('restaurant_info_page_text_service_instance_tag')}
                                                    </Typography>
                                                    <Typography sx={{ color: 'text.secondary', marginLeft: 2 }}>{(item?.business_labels || [])?.join('，')}</Typography>
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box className={'group-card'} sx={{ paddingLeft: 8 }}>
                                                {showWarrants.length > 0 ?
                                                    showWarrants.map((item, index) => (
                                                        <WarrantCard key={index} item={item} />
                                                    ))
                                                    : i18n.t('general_text_no_or_more_data')
                                                }
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>

                            )
                        }) :
                        (<Box className={'fade-in-right'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                            {i18n.t('general_text_no_or_more_data')}
                        </Box>)
                }
            </Box>
        </Box >
    )
};

// *********************
// TYPE
// *********************
type Data = Group;
// 下拉框类型
type FilterList = {
    value: string;
    label: string;
    disabled?: boolean;
}

type AccordionProps = {
    // 集团数据
    groupWarrant?: Pick<Group, '_id' | 'string_id' | 'nickname' | 'warrants'>;
    // 餐厅数据
    restaurantWarrant?: RestaurantsInGroup[];
    // 语言
    language?: string;
}